import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, ArrowUpDown, Search, Wand2, Mail, Lock } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUSES = ["all", "new", "contacted", "replied", "meeting_booked", "not_interested"];

const STATUS_LABEL = {
  new: "New",
  contacted: "Contacted",
  replied: "Replied",
  meeting_booked: "Meeting booked",
  not_interested: "Not interested",
};

function statusBadge(s) {
  const map = {
    new: "bg-primary/10 text-primary border-primary/20",
    contacted: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    replied: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    meeting_booked: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    not_interested: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
  };
  return `text-xs px-2 py-0.5 rounded border ${map[s] || "border-border"}`;
}

function toCSV(rows) {
  const cols = ["company","website","location","est_size","decision_maker_role","fit_score","why_good_fit","draft_email","draft_linkedin","source_url","status","notes"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = cols.join(",");
  const body = rows.map((r) => cols.map((c) => esc(r[c])).join(",")).join("\n");
  return header + "\n" + body;
}

export default function Results() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isFreeLocked = user && user.role !== "admin" && (user.plan === "free" || !user.plan);
  const [list, setList] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "fit_score", dir: "desc" });
  const [enrichingAll, setEnrichingAll] = useState(false);
  const [clientEmail, setClientEmail] = useState("");
  const [clientNote, setClientNote] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const reload = async () => {
    const b = await api.get(`/lead-lists/${id}/leads`);
    setLeads(b.data);
  };

  const enrichAll = async () => {
    setEnrichingAll(true);
    try {
      const { data } = await api.post(`/lead-lists/${id}/enrich-all`);
      toast.success(`Re-enriching ${data.total} leads in the background — refresh in a moment`);
      // Poll a few times to pick up the updates
      setTimeout(reload, 4000);
      setTimeout(reload, 12000);
      setTimeout(reload, 25000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start enrichment");
    } finally {
      setEnrichingAll(false);
    }
  };

  const sendToClient = async () => {
    if (!clientEmail) {
      toast.error("Enter a client email");
      return;
    }
    setSendingEmail(true);
    try {
      await api.post(`/lead-lists/${id}/email-to-client`, { email: clientEmail, note: clientNote });
      toast.success(`Sending ${leads.length} leads to ${clientEmail}`);
      setEmailOpen(false);
      setClientEmail("");
      setClientNote("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Email failed");
    } finally {
      setSendingEmail(false);
    }
  };

  useEffect(() => {
    (async () => {
      const [a, b] = await Promise.all([
        api.get(`/lead-lists/${id}`),
        api.get(`/lead-lists/${id}/leads`),
      ]);
      setList(a.data);
      setLeads(b.data);
    })();
  }, [id]);

  const filtered = useMemo(() => {
    let out = leads;
    if (filter !== "all") out = out.filter((l) => l.status === filter);
    if (q) {
      const term = q.toLowerCase();
      out = out.filter((l) => (l.company + " " + l.location + " " + l.decision_maker_role).toLowerCase().includes(term));
    }
    out = [...out].sort((x, y) => {
      const a = x[sort.key] ?? "";
      const b = y[sort.key] ?? "";
      if (typeof a === "number" && typeof b === "number") return sort.dir === "asc" ? a - b : b - a;
      return sort.dir === "asc" ? String(a).localeCompare(String(b)) : String(b).localeCompare(String(a));
    });
    return out;
  }, [leads, filter, q, sort]);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));
  };

  const downloadCSV = () => {
    const blob = new Blob([toCSV(filtered)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(list?.business_name || "leads").replace(/\s+/g, "_")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!list) return <div className="min-h-screen"><Nav /><div className="max-w-7xl mx-auto px-6 py-10 text-muted-foreground">Loading…</div></div>;

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <div className="overline text-primary">Results</div>
            <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tighter">{list.business_name}</h1>
            <div className="mt-2 text-sm text-muted-foreground">
              {list.industry} · {list.location} · {leads.length} leads
            </div>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9 w-56"
                placeholder="Search company…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="results-search"
              />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44" data-testid="results-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`filter-${s}`}>
                    {s === "all" ? "All statuses" : STATUS_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full border-primary/40 text-primary hover:bg-primary/10" data-testid="email-client-btn">
                  <Mail className="w-4 h-4 mr-1.5" /> Email to client
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="email-client-dialog">
                <DialogHeader>
                  <DialogTitle>Email this list to a client</DialogTitle>
                  <DialogDescription>They'll receive an HTML table of all leads sorted by fit score. Your email will be set as the reply-to.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="client-email">Client email</Label>
                    <Input id="client-email" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="client@company.com" data-testid="client-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="client-note">Note (optional)</Label>
                    <Textarea id="client-note" rows={3} value={clientNote} onChange={(e) => setClientNote(e.target.value)} placeholder="Hi — attaching this week's researched leads for your review." data-testid="client-note-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={sendToClient} disabled={sendingEmail} className="rounded-full bg-primary hover:bg-[#FF7043] text-white" data-testid="send-to-client-btn">
                    {sendingEmail ? "Sending…" : `Send ${leads.length} leads`}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button onClick={enrichAll} disabled={enrichingAll} variant="outline" className="rounded-full border-primary/40 text-primary hover:bg-primary/10" data-testid="enrich-all-btn">
              <Wand2 className="w-4 h-4 mr-1.5" /> {enrichingAll ? "Starting…" : "Re-enrich all"}
            </Button>
            <Button onClick={downloadCSV} className="rounded-full bg-primary hover:bg-[#FF7043] text-white" data-testid="download-csv-btn">
              <Download className="w-4 h-4 mr-1.5" /> CSV
            </Button>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-[#121214] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {[
                  ["company", "Company"],
                  ["location", "Location"],
                  ["decision_maker_role", "Role"],
                  ["fit_score", "Fit"],
                  ["status", "Status"],
                ].map(([k, label]) => (
                  <TableHead key={k} onClick={() => toggleSort(k)} className="cursor-pointer select-none" data-testid={`sort-${k}`}>
                    <span className="inline-flex items-center gap-1">{label} <ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                  </TableHead>
                ))}
                <TableHead className="text-right">Open</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No leads match these filters.</TableCell></TableRow>
              ) : filtered.map((l) => (
                <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className="hover:bg-white/[0.02]">
                  <TableCell>
                    <div className="font-medium">{l.company}</div>
                    {l.website && <a href={l.website} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-primary">{l.website}</a>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{l.location}</TableCell>
                  <TableCell className="text-sm">{l.decision_maker_role}</TableCell>
                  <TableCell className="mono">{l.fit_score}</TableCell>
                  <TableCell><span className={statusBadge(l.status)}>{STATUS_LABEL[l.status]}</span></TableCell>
                  <TableCell className="text-right">
                    {isFreeLocked ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full border-primary/40 text-primary hover:bg-primary/10"
                        onClick={() => nav("/pricing")}
                        data-testid={`locked-lead-${l.id}`}
                      >
                        <Lock className="w-3 h-3 mr-1" /> Unlock
                      </Button>
                    ) : (
                      <Button asChild size="sm" variant="outline" className="rounded-full" data-testid={`open-lead-${l.id}`}>
                        <Link to={`/leads/${l.id}`}>Open</Link>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">AI-generated; verify before contacting.</p>
        {isFreeLocked && (
          <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-6 flex items-start justify-between gap-6" data-testid="free-upgrade-banner">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 text-primary font-medium">
                <Lock className="w-4 h-4" /> Leads locked on the free plan
              </div>
              <div className="mt-2 text-sm text-muted-foreground leading-relaxed">
                You can generate lists for free, but opening a lead's drafts, LinkedIn URL, and copy-ready outreach requires a paid plan. Starter unlocks all leads for $29/mo.
              </div>
            </div>
            <Button onClick={() => nav("/pricing")} className="rounded-full bg-primary hover:bg-[#FF7043] text-white" data-testid="free-upgrade-cta">
              Upgrade to unlock
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
